import { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import { accessToken } from '@/lib/authToken'

/**
 * The AI CIO conversation, as a hook.
 *
 * Extracted so the full page and the docked pane are the same chat rather than two
 * implementations that drift. Each mount is its own conversation — the dock and the page
 * do not share a thread — but both read the same history from the server, so anything
 * either one says is visible from the other.
 *
 * Streaming is server-sent events over `fetch`, not `EventSource`: the endpoint is a POST
 * and needs an Authorization header, and EventSource supports neither.
 */

export interface CioMsg {
  role: 'user' | 'assistant'
  content: string
  tools?: string[]
  kb?: string
  error?: string
  streaming?: boolean
}

export interface CioStatus {
  available: boolean
  reason: string | null
  provider: string | null
  model: string | null
  n_tools: number
  groups: string[]
  kb_version: string
  max_tool_rounds: number
}

export function useCioChat(opts: { persistKey?: string } = {}) {
  const { persistKey } = opts
  const qc = useQueryClient()
  const [msgs, setMsgs] = useState<CioMsg[]>([])
  const [convId, setConvId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [activeTools, setActiveTools] = useState<string[]>([])
  const abortRef = useRef<AbortController | null>(null)

  const status = useQuery<CioStatus>({
    queryKey: ['cio-status'],
    queryFn: () => api.get('/ai-cio/status').then(r => r.data),
    staleTime: 60_000,
  })
  const conversations = useQuery({
    queryKey: ['cio-convs'],
    queryFn: () => api.get('/ai-cio/conversations').then(r => r.data.conversations),
  })

  const open = useCallback(async (id: string) => {
    abortRef.current?.abort()
    const d = await api.get(`/ai-cio/conversations/${id}`).then(r => r.data)
    setConvId(id)
    setMsgs((d.messages || []).map((m: any) => ({
      role: m.role, content: m.content, tools: m.tools_called || undefined,
      kb: m.kb_version || undefined, error: m.error || undefined,
    })))
  }, [])

  // The dock survives navigation but not a reload, so the thread it was on is remembered
  // and reopened. A conversation deleted elsewhere just fails to load, which is fine —
  // the dock starts empty rather than showing a thread that no longer exists.
  useEffect(() => {
    if (!persistKey) return
    let id: string | null = null
    try { id = localStorage.getItem(persistKey) } catch { /* private window */ }
    if (id) open(id).catch(() => { try { localStorage.removeItem(persistKey) } catch {} })
  }, [persistKey, open])

  useEffect(() => {
    if (!persistKey) return
    try {
      if (convId) localStorage.setItem(persistKey, convId)
      else localStorage.removeItem(persistKey)
    } catch { /* ignore */ }
  }, [persistKey, convId])

  useEffect(() => () => abortRef.current?.abort(), [])

  const reset = useCallback(() => {
    abortRef.current?.abort()
    setConvId(null); setMsgs([]); setActiveTools([]); setBusy(false)
  }, [])

  const remove = useCallback(async (id: string) => {
    await api.delete(`/ai-cio/conversations/${id}`)
    if (convId === id) reset()
    qc.invalidateQueries({ queryKey: ['cio-convs'] })
  }, [convId, qc, reset])

  const stop = useCallback(() => abortRef.current?.abort(), [])

  const send = useCallback(async (question: string) => {
    const q = question.trim()
    if (!q || busy) return
    setMsgs(m => [...m, { role: 'user', content: q },
                         { role: 'assistant', content: '', streaming: true }])
    setBusy(true); setActiveTools([])

    const ctrl = new AbortController()
    abortRef.current = ctrl
    const seen: string[] = []

    const patchLast = (fn: (m: CioMsg) => void) => setMsgs(m => {
      const next = [...m]
      const last = next[next.length - 1]
      if (last?.role === 'assistant') fn(last)
      return next
    })

    try {
      // Same token source as the axios client — this fetch bypasses the interceptor,
      // so without sharing it the chat would authenticate as a different identity than
      // the rest of the app once central login is in use.
      const token = await accessToken()
      const res = await fetch(`${import.meta.env.VITE_API_URL || '/api'}/ai-cio/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token ?? ''}`,
        },
        body: JSON.stringify({ message: q, conversation_id: convId }),
        signal: ctrl.signal,
      })
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`)

      const reader = res.body.getReader()
      const dec = new TextDecoder()
      let buf = ''
      for (;;) {
        const { done, value } = await reader.read()
        if (done) break
        buf += dec.decode(value, { stream: true })
        // SSE frames are separated by a blank line; a partial frame stays buffered.
        const frames = buf.split('\n\n')
        buf = frames.pop() ?? ''
        for (const frame of frames) {
          const line = frame.split('\n').find(l => l.startsWith('data: '))
          if (!line) continue
          let ev: any
          try { ev = JSON.parse(line.slice(6)) } catch { continue }

          if (ev.type === 'token') {
            patchLast(m => { m.content += ev.text })
          } else if (ev.type === 'tools') {
            seen.push(...(ev.names || []))
            setActiveTools([...seen])
          } else if (ev.type === 'done') {
            setConvId(ev.conversation_id)
            patchLast(m => {
              m.streaming = false; m.tools = ev.tools_called; m.kb = ev.kb_version
            })
            qc.invalidateQueries({ queryKey: ['cio-convs'] })
          } else if (ev.type === 'error') {
            patchLast(m => { m.streaming = false; m.error = ev.message })
          }
        }
      }
    } catch (e: any) {
      if (e?.name !== 'AbortError') {
        patchLast(m => { m.streaming = false; m.error = e?.message || 'Request failed' })
      } else {
        patchLast(m => { m.streaming = false })
      }
    } finally {
      setBusy(false); setActiveTools([]); abortRef.current = null
    }
  }, [busy, convId, qc])

  return {
    msgs, convId, busy, activeTools,
    status: status.data, statusLoading: status.isLoading,
    unavailable: !!status.data && !status.data.available,
    conversations: conversations.data || [],
    send, stop, reset, open, remove,
  }
}
